/**
 * Sound Manager for Hollowveil
 * Supports multiple audio channels that can play simultaneously:
 * - ambient: Background music/ambiance (loops, only one at a time)
 * - effect: Sound effects (can stack multiple)
 * - ui: UI sounds (clicks, hovers)
 * - voice: Voice/dialogue sounds
 */

import { useCallback, useEffect, useRef } from 'react';
import { create } from 'zustand';

// Types
type SoundChannel = 'ambient' | 'effect' | 'ui' | 'voice';

interface SoundState {
  masterVolume: number;
  channelVolumes: Record<SoundChannel, number>;
  muted: boolean;
  musicEnabled: boolean;
  soundEffectsEnabled: boolean;
  currentAmbient: string | null;
  setMasterVolume: (volume: number) => void;
  setChannelVolume: (channel: SoundChannel, volume: number) => void;
  setMuted: (muted: boolean) => void;
  setMusicEnabled: (enabled: boolean) => void;
  setSoundEffectsEnabled: (enabled: boolean) => void;
  setCurrentAmbient: (name: string | null) => void;
}

// Zustand store for sound state
export const useSoundStore = create<SoundState>((set) => ({
  masterVolume: 0.7,
  channelVolumes: {
    ambient: 0.5,
    effect: 0.8,
    ui: 0.6,
    voice: 1.0,
  },
  muted: false,
  musicEnabled: true,
  soundEffectsEnabled: true,
  currentAmbient: null,
  setMasterVolume: (volume) => set({ masterVolume: volume }),
  setChannelVolume: (channel, volume) =>
    set((state) => ({
      channelVolumes: { ...state.channelVolumes, [channel]: volume },
    })),
  setMuted: (muted) => set({ muted }),
  setMusicEnabled: (enabled) => set({ musicEnabled: enabled }),
  setSoundEffectsEnabled: (enabled) => set({ soundEffectsEnabled: enabled }),
  setCurrentAmbient: (name) => set({ currentAmbient: name }),
}));

// Audio pool for effect sounds (allows multiple simultaneous plays)
class AudioPool {
  private pool: HTMLAudioElement[] = [];
  private poolSize: number;

  constructor(poolSize = 10) {
    this.poolSize = poolSize;
  }

  getAvailable(): HTMLAudioElement {
    // Find an audio element that's not playing
    let audio = this.pool.find((a) => a.paused || a.ended);

    if (!audio && this.pool.length < this.poolSize) {
      // Create new audio element if pool not full
      audio = new Audio();
      this.pool.push(audio);
    }

    if (!audio) {
      // Reuse oldest if pool is full
      audio = this.pool[0];
      audio.pause();
      audio.currentTime = 0;
    }

    return audio;
  }

  stopAll() {
    this.pool.forEach((audio) => {
      audio.pause();
      audio.currentTime = 0;
    });
  }
}

// Audio effect types
type AudioEffectType = 'distortion' | 'lowpass' | 'highpass' | 'reverb';

interface AudioEffectOptions {
  type: AudioEffectType;
  amount?: number; // 0-1 for intensity
}

// Sound Manager singleton
class SoundManager {
  private static instance: SoundManager;
  private audioCache: Map<string, HTMLAudioElement> = new Map();
  private ambientAudio: HTMLAudioElement | null = null;
  private effectPool: AudioPool;
  private uiPool: AudioPool;
  private voiceAudio: HTMLAudioElement | null = null;
  private basePath = '/sounds/';
  private audioContext: AudioContext | null = null;

  private constructor() {
    this.effectPool = new AudioPool(10);
    this.uiPool = new AudioPool(5);
  }

  private getAudioContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }
    return this.audioContext;
  }

  // Create distortion curve for WaveShaperNode
  private makeDistortionCurve(amount: number): Float32Array {
    const samples = 44100;
    const curve = new Float32Array(samples);
    const k = amount * 100;

    for (let i = 0; i < samples; i++) {
      const x = (i * 2) / samples - 1;
      curve[i] = ((3 + k) * x * 20 * (Math.PI / 180)) / (Math.PI + k * Math.abs(x));
    }
    return curve;
  }

  static getInstance(): SoundManager {
    if (!SoundManager.instance) {
      SoundManager.instance = new SoundManager();
    }
    return SoundManager.instance;
  }

  private getVolume(channel: SoundChannel): number {
    const state = useSoundStore.getState();
    if (state.muted) return 0;

    // Check channel-specific enable flags
    if (channel === 'ambient' && !state.musicEnabled) return 0;
    if ((channel === 'effect' || channel === 'ui' || channel === 'voice') && !state.soundEffectsEnabled) return 0;

    return state.masterVolume * state.channelVolumes[channel];
  }

  private getSoundPath(name: string): string {
    // If it already has an extension, use as-is
    if (name.match(/\.(mp3|wav|ogg|webm)$/)) {
      return `${this.basePath}${name}`;
    }
    // Default to .mp3
    return `${this.basePath}${name}.mp3`;
  }

  /**
   * Preload a sound for faster playback
   */
  preload(name: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.audioCache.has(name)) {
        resolve();
        return;
      }

      const audio = new Audio(this.getSoundPath(name));
      audio.preload = 'auto';

      audio.oncanplaythrough = () => {
        this.audioCache.set(name, audio);
        resolve();
      };

      audio.onerror = () => {
        console.warn(`Failed to preload sound: ${name}`);
        reject(new Error(`Failed to preload: ${name}`));
      };
    });
  }

  /**
   * Preload multiple sounds
   */
  async preloadAll(names: string[]): Promise<void> {
    await Promise.allSettled(names.map((name) => this.preload(name)));
  }

  /**
   * Play an ambient/background sound (loops, replaces current ambient)
   */
  playAmbient(name: string, fadeIn = 1000): void {
    const state = useSoundStore.getState();

    // Fade out current ambient if playing
    if (this.ambientAudio && !this.ambientAudio.paused) {
      this.fadeOut(this.ambientAudio, 500).then(() => {
        this.ambientAudio?.pause();
      });
    }

    const audio = new Audio(this.getSoundPath(name));
    audio.loop = true;
    audio.volume = 0;

    this.ambientAudio = audio;
    state.setCurrentAmbient(name);

    audio.play().then(() => {
      this.fadeIn(audio, fadeIn, this.getVolume('ambient'));
    }).catch((err) => {
      console.warn('Failed to play ambient:', err);
    });
  }

  /**
   * Stop ambient sound
   */
  stopAmbient(fadeOut = 1000): void {
    if (this.ambientAudio) {
      this.fadeOut(this.ambientAudio, fadeOut).then(() => {
        this.ambientAudio?.pause();
        this.ambientAudio = null;
        useSoundStore.getState().setCurrentAmbient(null);
      });
    }
  }

  /**
   * Play a sound effect (can play multiple simultaneously)
   */
  playEffect(name: string, volume?: number): void {
    const audio = this.effectPool.getAvailable();
    audio.src = this.getSoundPath(name);
    audio.volume = volume ?? this.getVolume('effect');
    audio.currentTime = 0;

    audio.play().catch((err) => {
      console.warn('Failed to play effect:', err);
    });
  }

  /**
   * Play a sound effect with audio effects (distortion, filters, etc.)
   */
  playEffectWithFx(name: string, effects: AudioEffectOptions[], volume?: number): void {
    const ctx = this.getAudioContext();

    // Resume context if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    // Fetch and decode audio
    fetch(this.getSoundPath(name))
      .then(response => response.arrayBuffer())
      .then(arrayBuffer => ctx.decodeAudioData(arrayBuffer))
      .then(audioBuffer => {
        // Create source
        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;

        // Create gain node for volume
        const gainNode = ctx.createGain();
        gainNode.gain.value = volume ?? this.getVolume('effect');

        // Build effect chain
        let currentNode: AudioNode = source;

        effects.forEach(effect => {
          if (effect.type === 'distortion') {
            const distortion = ctx.createWaveShaper();
            distortion.curve = this.makeDistortionCurve(effect.amount ?? 0.5);
            distortion.oversample = '4x';
            currentNode.connect(distortion);
            currentNode = distortion;
          } else if (effect.type === 'lowpass') {
            const filter = ctx.createBiquadFilter();
            filter.type = 'lowpass';
            filter.frequency.value = 800 + (1 - (effect.amount ?? 0.5)) * 3000;
            filter.Q.value = 1;
            currentNode.connect(filter);
            currentNode = filter;
          } else if (effect.type === 'highpass') {
            const filter = ctx.createBiquadFilter();
            filter.type = 'highpass';
            filter.frequency.value = 100 + (effect.amount ?? 0.5) * 2000;
            filter.Q.value = 1;
            currentNode.connect(filter);
            currentNode = filter;
          } else if (effect.type === 'reverb') {
            // Simple reverb using convolver with generated impulse response
            const convolver = ctx.createConvolver();
            const reverbTime = 1 + (effect.amount ?? 0.5) * 3; // 1-4 seconds
            const sampleRate = ctx.sampleRate;
            const length = sampleRate * reverbTime;
            const impulse = ctx.createBuffer(2, length, sampleRate);

            // Generate impulse response
            for (let channel = 0; channel < 2; channel++) {
              const channelData = impulse.getChannelData(channel);
              for (let i = 0; i < length; i++) {
                // Exponential decay with random noise
                channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2);
              }
            }

            convolver.buffer = impulse;

            // Mix dry/wet signal
            const dryGain = ctx.createGain();
            const wetGain = ctx.createGain();
            const mixAmount = effect.amount ?? 0.5;
            dryGain.gain.value = 1 - mixAmount * 0.5;
            wetGain.gain.value = mixAmount;

            // Route: currentNode -> dryGain -> gainNode
            //        currentNode -> convolver -> wetGain -> gainNode
            currentNode.connect(dryGain);
            currentNode.connect(convolver);
            convolver.connect(wetGain);

            // Create a merger to combine dry and wet
            const merger = ctx.createGain();
            dryGain.connect(merger);
            wetGain.connect(merger);

            currentNode = merger;
          }
        });

        // Connect to gain and output
        currentNode.connect(gainNode);
        gainNode.connect(ctx.destination);

        // Play
        source.start(0);
      })
      .catch(err => {
        console.warn('Failed to play effect with fx:', err);
        // Fallback to normal playback
        this.playEffect(name, volume);
      });
  }

  /**
   * Play a UI sound
   */
  playUI(name: string): void {
    const audio = this.uiPool.getAvailable();
    audio.src = this.getSoundPath(name);
    audio.volume = this.getVolume('ui');
    audio.currentTime = 0;

    audio.play().catch((err) => {
      console.warn('Failed to play UI sound:', err);
    });
  }

  /**
   * Play a voice/dialogue sound (stops previous voice)
   */
  playVoice(name: string): void {
    if (this.voiceAudio) {
      this.voiceAudio.pause();
    }

    const audio = new Audio(this.getSoundPath(name));
    audio.volume = this.getVolume('voice');
    this.voiceAudio = audio;

    audio.play().catch((err) => {
      console.warn('Failed to play voice:', err);
    });
  }

  /**
   * Stop voice sound
   */
  stopVoice(): void {
    if (this.voiceAudio) {
      this.voiceAudio.pause();
      this.voiceAudio = null;
    }
  }

  /**
   * Play any sound (auto-detects channel from name prefix)
   */
  play(name: string, options?: { volume?: number; loop?: boolean }): void {
    // Detect channel from name prefix
    const prefix = name.split('_')[0];

    switch (prefix) {
      case 'ambient':
      case 'music':
        this.playAmbient(name);
        break;
      case 'ui':
        this.playUI(name);
        break;
      case 'voice':
      case 'npc':
        this.playVoice(name);
        break;
      default:
        // Everything else is an effect
        this.playEffect(name, options?.volume);
        break;
    }
  }

  /**
   * Stop all sounds
   */
  stopAll(): void {
    this.stopAmbient(0);
    this.effectPool.stopAll();
    this.uiPool.stopAll();
    this.stopVoice();
  }

  /**
   * Update volumes (call when volume settings change)
   */
  updateVolumes(): void {
    const state = useSoundStore.getState();

    if (this.ambientAudio) {
      // Stop ambient if music is disabled, otherwise update volume
      if (!state.musicEnabled) {
        this.ambientAudio.pause();
      } else {
        this.ambientAudio.volume = this.getVolume('ambient');
        // Resume if was paused due to music being disabled
        if (this.ambientAudio.paused && state.currentAmbient) {
          this.ambientAudio.play().catch(() => {});
        }
      }
    }
    if (this.voiceAudio) {
      this.voiceAudio.volume = this.getVolume('voice');
    }
  }

  // Fade helpers
  private fadeIn(audio: HTMLAudioElement, duration: number, targetVolume: number): Promise<void> {
    return new Promise((resolve) => {
      const startVolume = 0;
      const startTime = performance.now();

      const tick = () => {
        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        audio.volume = startVolume + (targetVolume - startVolume) * progress;

        if (progress < 1) {
          requestAnimationFrame(tick);
        } else {
          resolve();
        }
      };

      tick();
    });
  }

  private fadeOut(audio: HTMLAudioElement, duration: number): Promise<void> {
    return new Promise((resolve) => {
      const startVolume = audio.volume;
      const startTime = performance.now();

      const tick = () => {
        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        audio.volume = startVolume * (1 - progress);

        if (progress < 1) {
          requestAnimationFrame(tick);
        } else {
          resolve();
        }
      };

      tick();
    });
  }
}

/**
 * React hook for sound management
 */
export function useSounds() {
  const manager = useRef(SoundManager.getInstance());
  const state = useSoundStore();

  // Update volumes when settings change
  useEffect(() => {
    manager.current.updateVolumes();
  }, [state.masterVolume, state.channelVolumes, state.muted, state.musicEnabled, state.soundEffectsEnabled]);

  const play = useCallback((name: string, options?: { volume?: number }) => {
    manager.current.play(name, options);
  }, []);

  const playEffect = useCallback((name: string, volume?: number) => {
    manager.current.playEffect(name, volume);
  }, []);

  const playEffectWithFx = useCallback((name: string, effects: { type: 'distortion' | 'lowpass' | 'highpass'; amount?: number }[], volume?: number) => {
    manager.current.playEffectWithFx(name, effects, volume);
  }, []);

  const playUI = useCallback((name: string) => {
    manager.current.playUI(name);
  }, []);

  const playAmbient = useCallback((name: string, fadeIn?: number) => {
    manager.current.playAmbient(name, fadeIn);
  }, []);

  const stopAmbient = useCallback((fadeOut?: number) => {
    manager.current.stopAmbient(fadeOut);
  }, []);

  const playVoice = useCallback((name: string) => {
    manager.current.playVoice(name);
  }, []);

  const stopVoice = useCallback(() => {
    manager.current.stopVoice();
  }, []);

  const stopAll = useCallback(() => {
    manager.current.stopAll();
  }, []);

  const preload = useCallback((names: string | string[]) => {
    const namesArray = Array.isArray(names) ? names : [names];
    return manager.current.preloadAll(namesArray);
  }, []);

  return {
    // Playback
    play,
    playEffect,
    playEffectWithFx,
    playUI,
    playAmbient,
    stopAmbient,
    playVoice,
    stopVoice,
    stopAll,
    preload,

    // State
    masterVolume: state.masterVolume,
    channelVolumes: state.channelVolumes,
    muted: state.muted,
    musicEnabled: state.musicEnabled,
    soundEffectsEnabled: state.soundEffectsEnabled,
    currentAmbient: state.currentAmbient,

    // Setters
    setMasterVolume: state.setMasterVolume,
    setChannelVolume: state.setChannelVolume,
    setMuted: state.setMuted,
    setMusicEnabled: state.setMusicEnabled,
    setSoundEffectsEnabled: state.setSoundEffectsEnabled,
  };
}

// Export singleton for non-React usage
export const soundManager = SoundManager.getInstance();
